import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { estimateFromEntry, formatMoney, formatThaiDate, formatThaiMonth, toInputDate } from '../utils/coopEstimate';
import './CoopSavings.css';

// ประเภทบัญชีสหกรณ์: หุ้น (หักรายเดือน ทบยอดประมาณการได้) / เงินฝากออมทรัพย์พิเศษ (ฝากเท่าไหร่ก็ได้ ดอกเบี้ยสูง)
// / ยอดกู้ (ยอดหนี้ตามหนังสือ หักชำระรายเดือน → ลดยอดประมาณการให้)
const TYPE_META = {
    share: { label: 'หุ้นสหกรณ์', hint: 'หักซื้อหุ้นพร้อมเงินเดือนทุกเดือน — ระบบทบยอดประมาณการให้' },
    special: { label: 'เงินฝากออมทรัพย์พิเศษ', hint: 'ฝากเท่าไหร่ก็ได้ ดอกเบี้ยสูง — ระบบช่วยจำยอดตามหนังสือล่าสุด' },
    loan: { label: 'ยอดกู้สหกรณ์', hint: 'ยอดหนี้คงเหลือตามหนังสือ + หักชำระเดือนละเท่าไหร่ — ระบบลดยอดหนี้ประมาณการให้ทุกเดือน' }
};

const EMPTY_FORM = { account_type: 'share', confirmed_date: '', balance: '', monthly_share: '', note: '' };

const typeOf = (e) => (e.account_type === 'special' || e.account_type === 'loan' ? e.account_type : 'share');

function todayInput() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function TypeChip({ type }) {
    return (
        <span className={`coop-type-chip coop-type-${TYPE_META[type] ? type : 'share'}`}>
            {TYPE_META[type]?.label || type}
        </span>
    );
}

function CoopSavings() {
    const location = useLocation();
    const navigate = useNavigate();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState({ type: '', text: '' });

    const load = useCallback(async () => {
        try {
            const r = await api.get('/coop/me');
            setEntries(r.data.entries || []);
        } catch (e) {
            setMsg({ type: 'error', text: 'โหลดข้อมูลไม่สำเร็จ' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // entries เรียงวันที่ใหม่สุดก่อนจาก API — ตัวแรกของแต่ละประเภทคือรายการล่าสุด
    const latestShare = entries.find(e => typeOf(e) === 'share') || null;
    const latestSpecial = entries.find(e => typeOf(e) === 'special') || null;
    const latestLoan = entries.find(e => typeOf(e) === 'loan') || null;
    const estShare = estimateFromEntry(latestShare);
    const estLoan = estimateFromEntry(latestLoan);

    // ค่าหักรายเดือนมักไม่เปลี่ยน — เติมจากรายการล่าสุดของประเภทเดียวกันให้ก่อน
    const prefillMonthly = (accountType) => {
        const src = accountType === 'share' ? latestShare : accountType === 'loan' ? latestLoan : null;
        return src?.monthly_share != null ? String(src.monthly_share) : '';
    };

    const openCreate = (accountType = 'share') => {
        setEditingId(null);
        setForm({
            ...EMPTY_FORM,
            account_type: accountType,
            confirmed_date: todayInput(),
            monthly_share: prefillMonthly(accountType)
        });
        setMsg({ type: '', text: '' });
        setShowForm(true);
    };

    // ลิงก์จาก popup แจ้งข่าว เช่น /coop?new=loan → เปิดฟอร์มประเภทนั้นให้ทันที
    useEffect(() => {
        if (loading) return;
        const t = new URLSearchParams(location.search).get('new');
        if (t && TYPE_META[t]) {
            openCreate(t);
            navigate('/coop', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, location.search]);

    const openEdit = (e) => {
        setEditingId(e.id);
        setForm({
            account_type: typeOf(e),
            confirmed_date: toInputDate(e.confirmed_date),
            balance: String(e.balance),
            monthly_share: e.monthly_share != null ? String(e.monthly_share) : '',
            note: e.note || ''
        });
        setMsg({ type: '', text: '' });
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm(EMPTY_FORM);
    };

    const pickType = (key) => {
        if (key === form.account_type) return;
        // สลับประเภท: ค่าหักรายเดือนของหุ้น/กู้คนละความหมาย — เติมใหม่ตามประเภทที่เลือก
        setForm({ ...form, account_type: key, monthly_share: editingId ? (key === 'special' ? '' : form.monthly_share) : prefillMonthly(key) });
    };

    const submit = async (ev) => {
        ev.preventDefault();
        if (!form.confirmed_date) { setMsg({ type: 'error', text: 'กรุณาระบุวันที่ตามหนังสือยืนยันยอด' }); return; }
        if (form.balance === '' || isNaN(parseFloat(String(form.balance).replace(/,/g, '')))) {
            setMsg({ type: 'error', text: 'กรุณาระบุยอดเงินเป็นตัวเลข' });
            return;
        }
        setSaving(true);
        setMsg({ type: '', text: '' });
        try {
            const payload = {
                account_type: form.account_type,
                confirmed_date: form.confirmed_date,
                balance: String(form.balance).replace(/,/g, ''),
                monthly_share: form.account_type === 'special' || form.monthly_share === ''
                    ? null
                    : String(form.monthly_share).replace(/,/g, ''),
                note: form.note
            };
            if (editingId) {
                await api.put(`/coop/entries/${editingId}`, payload);
                setMsg({ type: 'success', text: 'แก้ไขรายการเรียบร้อย' });
            } else {
                await api.post('/coop/entries', payload);
                setMsg({ type: 'success', text: 'บันทึกยอดเรียบร้อย' });
            }
            cancelForm();
            await load();
        } catch (e) {
            setMsg({ type: 'error', text: e.response?.data?.error || 'บันทึกไม่สำเร็จ' });
        } finally {
            setSaving(false);
        }
    };

    const remove = async (e) => {
        if (!window.confirm(`ลบรายการ${TYPE_META[typeOf(e)].label} ณ วันที่ ${formatThaiDate(e.confirmed_date)} ใช่หรือไม่?`)) return;
        try {
            await api.delete(`/coop/entries/${e.id}`);
            setMsg({ type: 'success', text: 'ลบรายการแล้ว' });
            await load();
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.error || 'ลบไม่สำเร็จ' });
        }
    };

    if (loading) {
        return <div className="coop-page"><div className="flex-center" style={{ padding: 40 }}><div className="spinner" /></div></div>;
    }

    const isSpecialForm = form.account_type === 'special';
    const isLoanForm = form.account_type === 'loan';

    return (
        <div className="coop-page">
            <div className="coop-head">
                <div>
                    <h1 className="coop-title">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M19 5H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z" />
                            <path d="M16 12h5" />
                            <circle cx="16" cy="12" r="1.5" />
                        </svg>
                        ยอดเงินสหกรณ์ออมทรัพย์ รพ.ศก.
                    </h1>
                    <p className="coop-sub">
                        ช่วยจำยอดจาก "หนังสือยืนยันยอด" ที่สหกรณ์ส่งมาให้ — บันทึกได้ทั้ง <strong>หุ้นสหกรณ์</strong>,
                        <strong> เงินฝากออมทรัพย์พิเศษ</strong> และ<strong> ยอดกู้สหกรณ์</strong> ระบบไม่ได้เชื่อมต่อกับสหกรณ์โดยตรง
                        ข้อมูลนี้เป็นของคุณคนเดียว
                    </p>
                </div>
                {!showForm && (
                    <button type="button" className="steam-btn steam-btn-primary" onClick={() => openCreate('share')}>
                        + บันทึกยอดจากหนังสือฉบับใหม่
                    </button>
                )}
            </div>

            {msg.text && (
                <div className={`alert ${msg.type === 'error' ? 'alert-error' : 'alert-success'}`}>{msg.text}</div>
            )}

            {showForm && (
                <form className="steam-card coop-form" onSubmit={submit}>
                    <div className="steam-card-header">
                        <h3>{editingId ? 'แก้ไขรายการ' : 'บันทึกยอดจากหนังสือยืนยันยอด'}</h3>
                    </div>
                    <div className="steam-card-body">
                        {/* เลือกประเภทบัญชี */}
                        <div className="form-group">
                            <label className="form-label">ประเภทบัญชี</label>
                            <div className="coop-type-picker">
                                {Object.entries(TYPE_META).map(([key, meta]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`coop-type-btn ${form.account_type === key ? 'active' : ''}`}
                                        onClick={() => pickType(key)}
                                    >
                                        {meta.label}
                                    </button>
                                ))}
                            </div>
                            <small className="coop-hint">{TYPE_META[form.account_type].hint}</small>
                        </div>

                        <div className="coop-form-grid">
                            <div className="form-group">
                                <label className="form-label">วันที่ตามหนังสือยืนยันยอด <span className="coop-req">*</span></label>
                                <input
                                    type="date"
                                    className="steam-input"
                                    value={form.confirmed_date}
                                    max={todayInput()}
                                    onChange={(e) => setForm({ ...form, confirmed_date: e.target.value })}
                                    required
                                />
                                <small className="coop-hint">"ณ วันที่ ..." ที่ระบุในหนังสือ</small>
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    {isLoanForm ? 'ยอดหนี้คงเหลือ (บาท)' : isSpecialForm ? 'ยอดเงินฝาก (บาท)' : 'ยอดเงิน / มูลค่าหุ้น (บาท)'} <span className="coop-req">*</span>
                                </label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    className="steam-input"
                                    placeholder={isLoanForm ? 'เช่น 500,000.00' : isSpecialForm ? 'เช่น 322,748.22' : 'เช่น 250,000.00'}
                                    value={form.balance}
                                    onChange={(e) => setForm({ ...form, balance: e.target.value })}
                                    required
                                />
                            </div>
                            {!isSpecialForm && (
                                <div className="form-group">
                                    <label className="form-label">
                                        {isLoanForm ? 'หักชำระเดือนละ (บาท)' : 'หักซื้อหุ้นเดือนละ (บาท)'} <span className="text-muted">— ไม่บังคับ</span>
                                    </label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        className="steam-input"
                                        placeholder={isLoanForm ? 'เช่น 8,500' : 'เช่น 1,000'}
                                        value={form.monthly_share}
                                        onChange={(e) => setForm({ ...form, monthly_share: e.target.value })}
                                    />
                                    <small className="coop-hint">
                                        {isLoanForm
                                            ? 'ถ้าระบุ ระบบจะลดยอดหนี้ให้ทุกสิ้นเดือน และบอกว่าเหลืออีกกี่งวด'
                                            : 'ถ้าระบุ ระบบจะทบยอดให้ทุกสิ้นเดือนโดยอัตโนมัติ'}
                                    </small>
                                </div>
                            )}
                            <div className="form-group">
                                <label className="form-label">หมายเหตุ <span className="text-muted">— ไม่บังคับ</span></label>
                                <input
                                    type="text"
                                    className="steam-input"
                                    maxLength={300}
                                    placeholder={isLoanForm ? 'เช่น สัญญาเงินกู้สามัญ เลขที่ ...' : isSpecialForm ? 'เช่น เลขสมาชิก 2714' : 'เช่น เลขที่สมาชิก'}
                                    value={form.note}
                                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="coop-form-actions">
                            <button type="button" className="steam-btn steam-btn-secondary" onClick={cancelForm} disabled={saving}>ยกเลิก</button>
                            <button type="submit" className="steam-btn steam-btn-primary" disabled={saving}>
                                {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                            </button>
                        </div>
                    </div>
                </form>
            )}

            {entries.length === 0 ? (
                !showForm && (
                    <div className="steam-card coop-empty">
                        <div className="steam-card-body">
                            <p>ยังไม่มีรายการ — เมื่อได้รับหนังสือยืนยันยอดจากสหกรณ์ ให้กรอกยอดและวันที่ไว้
                                (หุ้นสหกรณ์ เงินฝากออมทรัพย์พิเศษ หรือยอดกู้) ระบบจะช่วยจำและคำนวณยอดประมาณการให้</p>
                            <div className="coop-empty-actions">
                                <button type="button" className="steam-btn steam-btn-primary" onClick={() => openCreate('share')}>เริ่มบันทึกยอดหุ้น</button>
                                <button type="button" className="steam-btn steam-btn-secondary" onClick={() => openCreate('loan')}>บันทึกยอดกู้</button>
                            </div>
                        </div>
                    </div>
                )
            ) : (
                <div className="coop-summary">
                    {latestShare && (
                        <>
                            <div className="steam-card coop-stat coop-stat-main">
                                <div className="coop-stat-label"><TypeChip type="share" /> ยอดประมาณการวันนี้</div>
                                <div className="coop-stat-value">{formatMoney(estShare.estimated)} <span>บาท</span></div>
                                {estShare.hasMonthly ? (
                                    <div className="coop-stat-note">
                                        ยอดยืนยัน {formatMoney(estShare.balance)} ณ {formatThaiDate(latestShare.confirmed_date)}
                                        {' '}+ หักซื้อหุ้น {formatMoney(estShare.monthly)} × {estShare.months} เดือน ({formatMoney(estShare.added)})
                                    </div>
                                ) : (
                                    <div className="coop-stat-note">
                                        ยังไม่ได้ระบุยอดหักซื้อหุ้นรายเดือน — แสดงยอดตามหนังสือ ณ {formatThaiDate(latestShare.confirmed_date)}
                                        {' '}<button type="button" className="coop-link" onClick={() => openEdit(latestShare)}>ระบุตอนนี้</button>
                                    </div>
                                )}
                            </div>
                            <div className="steam-card coop-stat">
                                <div className="coop-stat-label">หักซื้อหุ้นเดือนละ</div>
                                <div className="coop-stat-value coop-stat-value-sm">
                                    {estShare.hasMonthly ? <>{formatMoney(estShare.monthly)} <span>บาท</span></> : <span className="text-muted">ไม่ระบุ</span>}
                                </div>
                                <div className="coop-stat-note">ทบให้ทุกสิ้นเดือนหลังวันที่ในหนังสือ</div>
                            </div>
                        </>
                    )}

                    {latestSpecial ? (
                        <div className="steam-card coop-stat coop-stat-special">
                            <div className="coop-stat-label"><TypeChip type="special" /> ยอดตามหนังสือล่าสุด</div>
                            <div className="coop-stat-value coop-stat-value-special">{formatMoney(latestSpecial.balance)} <span>บาท</span></div>
                            <div className="coop-stat-note">
                                ณ วันที่ {formatThaiDate(latestSpecial.confirmed_date)} — ฝากเท่าไหร่ก็ได้ ดอกเบี้ยสูง
                                (ยังไม่รวมดอกเบี้ย/ยอดฝากหลังจากนั้น)
                            </div>
                        </div>
                    ) : (
                        <button type="button" className="steam-card coop-stat coop-stat-add" onClick={() => openCreate('special')}>
                            <div className="coop-stat-label"><TypeChip type="special" /></div>
                            <div className="coop-stat-add-text">+ บันทึกยอดเงินฝากออมทรัพย์พิเศษ</div>
                            <div className="coop-stat-note">มีบัญชีเงินฝากออมทรัพย์พิเศษด้วยไหม? จดยอดไว้ที่นี่ได้เลย</div>
                        </button>
                    )}

                    {latestLoan ? (
                        <div className="steam-card coop-stat coop-stat-loan">
                            <div className="coop-stat-label"><TypeChip type="loan" /> ยอดหนี้คงเหลือประมาณการ</div>
                            <div className="coop-stat-value coop-stat-value-loan">{formatMoney(estLoan.estimated)} <span>บาท</span></div>
                            {estLoan.hasMonthly ? (
                                <div className="coop-stat-note">
                                    ยอดหนี้ {formatMoney(estLoan.balance)} ณ {formatThaiDate(latestLoan.confirmed_date)}
                                    {' '}− หักชำระ {formatMoney(estLoan.monthly)} × {estLoan.months} เดือน ({formatMoney(estLoan.paid)})
                                    <br />
                                    {estLoan.estimated > 0
                                        ? <>เหลืออีกประมาณ <strong>{estLoan.monthsLeft} งวด</strong> (ครบราว {formatThaiMonth(estLoan.payoffDate)})</>
                                        : <strong>ผ่อนครบแล้วตามประมาณการ 🎉</strong>}
                                </div>
                            ) : (
                                <div className="coop-stat-note">
                                    ยังไม่ได้ระบุยอดหักชำระรายเดือน — แสดงยอดตามหนังสือ ณ {formatThaiDate(latestLoan.confirmed_date)}
                                    {' '}<button type="button" className="coop-link" onClick={() => openEdit(latestLoan)}>ระบุตอนนี้</button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <button type="button" className="steam-card coop-stat coop-stat-add" onClick={() => openCreate('loan')}>
                            <div className="coop-stat-label"><TypeChip type="loan" /></div>
                            <div className="coop-stat-add-text">+ บันทึกยอดกู้สหกรณ์</div>
                            <div className="coop-stat-note">มียอดกู้กับสหกรณ์ไหม? ใส่ยอดหนี้และยอดหักต่อเดือน ระบบลดยอดให้ทุกเดือน</div>
                        </button>
                    )}

                    {!latestShare && (
                        <button type="button" className="steam-card coop-stat coop-stat-add" onClick={() => openCreate('share')}>
                            <div className="coop-stat-label"><TypeChip type="share" /></div>
                            <div className="coop-stat-add-text">+ บันทึกยอดหุ้นสหกรณ์</div>
                            <div className="coop-stat-note">ระบุยอดหักรายเดือนแล้วระบบทบยอดประมาณการให้</div>
                        </button>
                    )}
                </div>
            )}

            {entries.length > 0 && (
                <p className="coop-disclaimer">
                    * ยอดประมาณการไม่รวมเงินปันผล เฉลี่ยคืน ดอกเบี้ย หรือรายการอื่นที่สหกรณ์ปรับปรุง
                    {latestLoan && ' — ยอดกู้: ถ้ายอดที่หักแต่ละเดือนรวมดอกเบี้ยด้วย ยอดหนี้จริงจะลดช้ากว่าที่แสดง'}
                    {' '}โปรดยึดยอดตามหนังสือยืนยันจากสหกรณ์เป็นหลัก และบันทึกยอดใหม่ทุกครั้งที่ได้รับหนังสือ
                </p>
            )}

            {entries.length > 0 && (
                <div className="steam-card">
                    <div className="steam-card-header"><h3>ประวัติหนังสือยืนยันยอด</h3></div>
                    <div className="steam-card-body coop-table-wrap">
                        <table className="steam-table coop-table">
                            <thead>
                                <tr>
                                    <th>ประเภท</th>
                                    <th>ณ วันที่</th>
                                    <th className="text-right">ยอดเงิน (บาท)</th>
                                    <th className="text-right">หักเดือนละ</th>
                                    <th>หมายเหตุ</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((e) => {
                                    const isLatest = e.id === latestShare?.id || e.id === latestSpecial?.id || e.id === latestLoan?.id;
                                    return (
                                        <tr key={e.id} className={isLatest ? 'coop-row-latest' : ''}>
                                            <td><TypeChip type={typeOf(e)} /></td>
                                            <td>
                                                {formatThaiDate(e.confirmed_date)}
                                                {isLatest && <span className="badge badge-primary coop-badge">ล่าสุด</span>}
                                            </td>
                                            <td className="text-right">{formatMoney(e.balance)}</td>
                                            <td className="text-right">{e.monthly_share != null ? formatMoney(e.monthly_share) : <span className="text-muted">-</span>}</td>
                                            <td className="coop-note">{e.note || <span className="text-muted">-</span>}</td>
                                            <td className="coop-actions">
                                                <button type="button" className="coop-link" onClick={() => openEdit(e)}>แก้ไข</button>
                                                <button type="button" className="coop-link coop-link-danger" onClick={() => remove(e)}>ลบ</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CoopSavings;
